/**
 * Push-to-talk dictation via the browser's Web Speech API.
 *
 * An operator's hands are often on other controls, so speaking a question to
 * the duty officer beats typing it. This wraps SpeechRecognition into a small
 * start/stop hook that streams the transcript back.
 *
 * Honest about support: Web Speech recognition is a Chrome/Edge feature and is
 * absent in Firefox and much of Safari. `supported` says so, and the caller is
 * expected to hide the control rather than offer a button that does nothing —
 * a dead mic is a worse affordance than none.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
};

function recognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface VoiceInput {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
  error: string | null;
}

export function useVoiceInput(onTranscript: (text: string) => void): VoiceInput {
  const [supported] = useState(() => recognitionCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    setError(null);
    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      const text = last?.[0]?.transcript?.trim();
      if (text) onTranscript(text);
    };
    recognition.onerror = (event) => setError(event.error);
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [onTranscript]);

  // Never leave the mic hot if the panel unmounts mid-listen.
  useEffect(() => () => recognitionRef.current?.stop(), []);

  return { supported, listening, start, stop, error };
}
